import { LightSystem } from './light-system/LightSystem.js';
import { ViewerFactory } from '../utility/ViewerFactory.js';
import { AmbientLightController } from './AmbientLightController.js';
import { EnvironmentManager } from './EnvironmentManager.js';
import { assert } from '../utility/Assert.js';
import { LightType } from './types/LightType.js';
import { PointLight } from './light-system/lights/PointLight.js';
import { DirectionalLight } from './light-system/lights/DirectionalLight.js';
import { SpotLight } from './light-system/lights/SpotLight.js';
import { LineLight } from './light-system/lights/LineLight.js';
import { DirectedShadowPointLight } from './light-system/lights/DirectedShadowPointLight.js';
import { get_hardware_limits } from '../utility/HardwareLimits.js';
export const LIGHT_CONSTRUCTORS = {
    [LightType.POINT_LIGHT]: PointLight,
    [LightType.DIRECTIONAL_LIGHT]: DirectionalLight,
    [LightType.SPOT_LIGHT]: SpotLight,
    [LightType.LINE_LIGHT]: LineLight,
    [LightType.DIRECTED_SHADOW_POINT_LIGHT]: DirectedShadowPointLight
};
/**
 * A manager for lights in the viewer.
 */
export class LightManager {
    constructor(_viewer) {
        this._viewer = _viewer;
        this.experimental_soft_shadow_renderers = new Set();
        const { MAX_LIGHTS, MAX_SHADOWCASTING_LIGHTS } = get_hardware_limits(_viewer);
        this._max_lights = MAX_LIGHTS;
        this.light_system = new LightSystem(MAX_SHADOWCASTING_LIGHTS, _viewer);
        this.ambient_light = new AmbientLightController(_viewer);
        this.environment = new EnvironmentManager(_viewer, this.ambient_light);
        this._factory = new ViewerFactory(LIGHT_CONSTRUCTORS, _viewer);
    }
    /**
     * Creates a light of the specified type.
     * @param type The type of light to create.
     * @param args The arguments to pass to the light constructor.
     * @throws An error if the maximum number of lights has been reached.
     */
    create_light(type, ...args) {
        assert(this.can_create_light(), `Maximum number of lights (${this._max_lights}) has been reached`);
        const light = this._factory.create(type, ...args);
        this.light_system.add_light(light);
        return light;
    }
    can_create_light() {
        const main_light_name = this.ambient_light.name;
        return this._viewer.scene.lights.filter(light => light.name !== main_light_name).length < this._max_lights;
    }
    remove_all_lights() {
        this.light_system.dispose_all_lights();
    }
    delete_light(light) {
        if (this.light_system.has_light(light)) {
            light.dispose();
            return true;
        }
        return false;
    }
    dispose() {
        this.light_system.dispose();
        this.ambient_light.dispose();
        this.environment.dispose();
    }
}
//# sourceMappingURL=LightManager.js.map